import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { card, h2, btn, muted } from '../pages/tools/ui';
import ClientSendPanel from './ClientSendPanel';

const W = 1080;
const H = 1350;

const loadImg = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = reject;
  img.src = src;
});

const wrapText = (ctx, text, x, y, maxW, lineH) => {
  const words = String(text).split(' ');
  let line = '';
  let yy = y;
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxW && line) {
      ctx.fillText(line, x, yy);
      line = w;
      yy += lineH;
    } else {
      line = test;
    }
  });
  if (line) ctx.fillText(line, x, yy);
};

// Shrinks the font until the text fits its width, so nothing runs into its neighbours.
const fitFont = (ctx, text, maxW, size, weight = '') => {
  let px = size;
  ctx.font = `${weight} ${px}px Arial`.trim();
  while (ctx.measureText(String(text)).width > maxW && px > 20) {
    px -= 2;
    ctx.font = `${weight} ${px}px Arial`.trim();
  }
  return px;
};

const drawReport = async (canvas, { title, rows, details, detailsTitle, rowsTitle, highlight, note, agent, photoUrl, logoSrc, height }) => {
  const reportH = height || H;
  canvas.width = W;
  canvas.height = reportH;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, W, reportH);
  ctx.fillStyle = '#D4AF37';
  ctx.fillRect(0, 0, W, 14);

  // Logo: fit inside a 340 x 120 box, keeping its proportions.
  try {
    const logo = await loadImg(logoSrc || '/resi-logo-black.png');
    const scale = Math.min(340 / logo.width, 120 / logo.height);
    ctx.drawImage(logo, 80, 60, logo.width * scale, logo.height * scale);
  } catch (e) { /* logo optional */ }

  if (photoUrl) {
    try {
      const ph = await loadImg(photoUrl);
      const cx = W - 150, cy = 150, r = 95;
      const s = Math.max((r * 2) / ph.width, (r * 2) / ph.height);
      const dw = ph.width * s, dh = ph.height * s;
      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(ph, cx - dw / 2, cy - dh / 2, dw, dh);
      ctx.restore();
      ctx.strokeStyle = '#D4AF37';
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    } catch (e) { /* photo optional */ }
  }

  // Title and date
  ctx.textAlign = 'left';
  ctx.fillStyle = '#1a1a1a';
  fitFont(ctx, title, 680, 60, 'bold');
  ctx.fillText(title, 80, 270);
  ctx.fillStyle = '#7f7f7f';
  fitFont(ctx, 'x', W, 28);
  ctx.fillText(new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }), 80, 322);

  // Highlight box with the main figure
  let y = 360;
  ctx.fillStyle = '#faf8f3';
  ctx.fillRect(80, y, W - 160, 130);
  ctx.fillStyle = '#D4AF37';
  ctx.fillRect(80, y, 10, 130);
  ctx.textAlign = 'left';
  ctx.fillStyle = '#1a1a1a';
  fitFont(ctx, highlight.label, 430, 38, 'bold');
  ctx.fillText(highlight.label, 130, y + 78);
  ctx.textAlign = 'right';
  fitFont(ctx, highlight.value, 380, 60, 'bold');
  ctx.fillText(highlight.value, W - 120, y + 84);
  y += 130 + 60;

  // Section writer: a label on the left, a value on the right, with a rule under each line.
  const section = (heading, items, rowH) => {
    if (heading) {
      ctx.textAlign = 'left';
      ctx.fillStyle = '#8C6D1F';
      fitFont(ctx, heading, W - 160, 34, 'bold');
      ctx.fillText(heading, 80, y);
      y += 30;
    }
    items.forEach((r) => {
      ctx.textAlign = 'left';
      ctx.fillStyle = '#444444';
      fitFont(ctx, r.label, 600, 32);
      ctx.fillText(r.label, 80, y + 26);
      ctx.textAlign = 'right';
      ctx.fillStyle = '#1a1a1a';
      fitFont(ctx, r.value, 380, 32, 'bold');
      ctx.fillText(r.value, W - 80, y + 26);
      ctx.strokeStyle = '#eee6d3';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(80, y + rowH - 14);
      ctx.lineTo(W - 80, y + rowH - 14);
      ctx.stroke();
      y += rowH;
    });
    y += 30;
  };

  if (details && details.length) section(detailsTitle, details, 60);
  section(rowsTitle, rows, 62);

  // Note, kept above the footer
  const fy = reportH - 230;
  if (note) {
    ctx.textAlign = 'left';
    ctx.fillStyle = '#7f7f7f';
    ctx.font = '26px Arial';
    wrapText(ctx, note, 80, Math.min(y, fy - 110), W - 160, 36);
  }

  // Agent footer
  ctx.fillStyle = '#e5dcc6';
  ctx.fillRect(80, fy, W - 160, 2);
  ctx.textAlign = 'left';
  ctx.fillStyle = '#1a1a1a';
  fitFont(ctx, agent.name || '', W - 160, 46, 'bold');
  ctx.fillText(agent.name || '', 80, fy + 70);
  ctx.fillStyle = '#444444';
  const contactLine = [agent.phone, agent.email].filter(Boolean).join('   |   ');
  fitFont(ctx, contactLine, W - 160, 32);
  ctx.fillText(contactLine, 80, fy + 125);
  ctx.fillStyle = '#8C6D1F';
  ctx.font = 'bold 32px Arial';
  ctx.fillText('RESIDENCE | eXp Realty', 80, fy + 175);
  return reportH;
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

// Builds a one-page PDF that embeds the JPEG image at full page size.
const makePdf = async (jpegBlob, pageW, pageH) => {
  const jpg = new Uint8Array(await jpegBlob.arrayBuffer());
  const enc = new TextEncoder();
  const parts = [];
  const offsets = [];
  let len = 0;
  const push = (x) => {
    const bytes = typeof x === 'string' ? enc.encode(x) : x;
    parts.push(bytes);
    len += bytes.length;
  };
  const obj = (n, body) => {
    offsets[n] = len;
    push(`${n} 0 obj\n${body}\nendobj\n`);
  };

  push('%PDF-1.4\n');
  obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
  obj(2, '<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
  obj(3, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageW} ${pageH}] /Resources << /XObject << /Im0 4 0 R >> >> /Contents 5 0 R >>`);
  offsets[4] = len;
  push(`4 0 obj\n<< /Type /XObject /Subtype /Image /Width ${pageW} /Height ${pageH} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpg.length} >>\nstream\n`);
  push(jpg);
  push('\nendstream\nendobj\n');
  const content = `q ${pageW} 0 0 ${pageH} 0 0 cm /Im0 Do Q`;
  obj(5, `<< /Length ${content.length} >>\nstream\n${content}\nendstream`);

  const xref = len;
  let table = 'xref\n0 6\n0000000000 65535 f \n';
  for (let i = 1; i <= 5; i += 1) {
    table += `${String(offsets[i]).padStart(10, '0')} 00000 n \n`;
  }
  push(table);
  push(`trailer\n<< /Size 6 /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`);
  return new Blob(parts, { type: 'application/pdf' });
};

const download = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export default function ShareReport({ title, rows, details, detailsTitle, rowsTitle, highlight, note, logoSrc, height, fileBaseName, defaultMessage }) {
  const { displayName, profile, user } = useAuth();
  // Agent name, phone, email, and photo come straight from the signed-in profile.
  const agent = { name: displayName || '', phone: profile?.phone || '', email: profile?.contactEmail || user?.email || '' };
  const photoUrl = profile?.photoUrl || null;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  const render = async () => {
    const canvas = document.createElement('canvas');
    await drawReport(canvas, { title, rows, details, detailsTitle, rowsTitle, highlight, note, agent, photoUrl, logoSrc, height });
    return canvas;
  };

  // Shows the page as it will download, so it can be checked before saving.
  const doPreview = async () => {
    setBusy(true);
    setMsg('');
    try {
      const canvas = await render();
      const blob = await canvasToBlob(canvas, 'image/png');
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
    } finally {
      setBusy(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  const doPng = async () => {
    setBusy(true);
    setMsg('');
    try {
      const canvas = await render();
      download(await canvasToBlob(canvas, 'image/png'), 'estimate.png');
    } finally {
      setBusy(false);
    }
  };

  const doPdf = async () => {
    setBusy(true);
    setMsg('');
    try {
      const canvas = await render();
      const jpg = await canvasToBlob(canvas, 'image/jpeg', 0.92);
      download(await makePdf(jpg, canvas.width, canvas.height), 'estimate.pdf');
    } finally {
      setBusy(false);
    }
  };

  const doShare = async () => {
    setBusy(true);
    setMsg('');
    try {
      const canvas = await render();
      const blob = await canvasToBlob(canvas, 'image/png');
      const file = new File([blob], 'estimate.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title });
      } else {
        download(blob, 'estimate.png');
        setMsg('Sharing files is not supported in this browser, so the image was downloaded instead.');
      }
    } catch (e) {
      if (e.name !== 'AbortError') setMsg('Could not share. Try downloading the image instead.');
    } finally {
      setBusy(false);
    }
  };

  const buildPdfFile = async () => {
    const canvas = await render();
    const jpg = await canvasToBlob(canvas, 'image/jpeg', 0.92);
    const blob = await makePdf(jpg, canvas.width, canvas.height);
    return new File([blob], `${fileBaseName || 'Estimate'}.pdf`, { type: 'application/pdf' });
  };

  const buildMessage = (client, channel) => (defaultMessage
    ? defaultMessage(client, channel, agent)
    : `Hi ${(client?.name || '').split(' ')[0]}, I've put together an estimate for you and I've ${channel === 'text' ? 'linked' : 'attached'} it below. I'd love to review it together.\n\n${agent.name}${agent.phone ? `\nCall or text: ${agent.phone}` : ''}`);

  return (
    <div style={card}>
      <h2 style={h2}>Share this estimate</h2>
      <p style={muted}>Your name, phone, email, and photo come from your profile.</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 16 }}>
        <button onClick={doPreview} disabled={busy} style={btn}>Preview PDF</button>
        <button onClick={doPreview} disabled={busy} style={btn}>Preview image</button>
        <button onClick={doPdf} disabled={busy} style={btn}>Download PDF</button>
        <button onClick={doPng} disabled={busy} style={btn}>Download image</button>
        <button onClick={doShare} disabled={busy} style={btn}>Share</button>
      </div>
      {previewUrl && (
        <div style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <span style={muted}>This is how the PDF will look.</span>
            <button onClick={closePreview} style={btn}>Close preview</button>
          </div>
          <img src={previewUrl} alt="Report preview" style={{ width: '100%', maxWidth: 540, display: 'block', border: '1px solid #e5dcc6', borderRadius: 8 }} />
        </div>
      )}
      <ClientSendPanel getFile={buildPdfFile} fileName={`${fileBaseName || 'Estimate'}.pdf`} buildMessage={buildMessage} />
      <p style={{ ...muted, marginTop: 12, marginBottom: 0 }}>Share attaches the image directly on phones. Text and email open a message, so attach the downloaded file before sending.</p>
      {msg && <p style={{ ...muted, color: '#8C6D1F', marginTop: 8 }}>{msg}</p>}
    </div>
  );
}
